"use client";

import { SafeHydrate } from "@/SafeHydrate";
import * as React from "react";
import { ArrowLeft, ArrowRight, Calendar, MapPin } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Carousel, CarouselContent, CarouselItem, type CarouselApi } from "@/components/ui/carousel";
import { Progress } from "@/components/ui/progress";
const events = [{
  title: "46th Annual Gala: Legado & Futuro",
  date: "October 14, 2023",
  location: "Denver Convention Center",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1208726/img/cohcc_gala_event.png",
  link: "/JoinUsPage"
}, {
  title: "State of Hispanic Business Summit",
  date: "August 22, 2023",
  location: "Grand Hyatt Denver",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1208726/img/business_summit_speaker.png",
  link: "/JoinUsPage"
}, {
  title: "Legislative Breakfast & Policy Briefing",
  date: "February 10, 2024",
  location: "State Capitol",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1208726/img/legislative_breakfast.png",
  link: "/JoinUsPage"
}, {
  title: "Sabor de Colorado: Culinary Festival",
  date: "May 05, 2024",
  location: "Civic Center Park",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1208726/img/hispanic_heritage_festival.png",
  link: "/JoinUsPage"
}, {
  title: "Women in Leadership Symposium",
  date: "March 08, 2024",
  location: "The Ritz-Carlton",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1208726/img/women_leadership_panel.png",
  link: "/JoinUsPage"
}];
export default function CommunityPulseEvents() {
  const [api, setApi] = React.useState<CarouselApi>();
  const [current, setCurrent] = React.useState(0);
  const [count, setCount] = React.useState(0);
  React.useEffect(() => {
    if (!api) {
      return;
    }
    setCount(api.scrollSnapList().length);
    setCurrent(api.selectedScrollSnap() + 1);
    api.on("select", () => {
      setCurrent(api.selectedScrollSnap() + 1);
    });
  }, [api]);
  const progressValue = count > 0 ? current / count * 100 : 0;
  return <section id="community-pulse-events" className="dark bg-background text-foreground py-20 overflow-hidden">
      <div className="container mx-auto px-4">
        <div className="mb-12 flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div className="max-w-2xl">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4 text-white">
              Community Pulse & Events
            </h2>
            <p className="text-muted-foreground text-lg">
              Connect with Colorado's most influential Hispanic leaders and
              entrepreneurs at our signature events.
            </p>
          </div>
          <div className="hidden md:block">
            <Link to="/JoinUsPage">
              <Button variant="outline" className="border-white/20 text-white hover:bg-white/10 hover:text-white">
                View Full Calendar
              </Button>
            </Link>
          </div>
        </div>

        <Carousel setApi={setApi} opts={{
        align: "start",
        loop: true
      }} className="w-full">
          <CarouselContent className="-ml-4">
            {events.map((event, index) => <CarouselItem key={index} data-index={index} className="pl-4 md:basis-1/2 lg:basis-1/3 xl:basis-1/4">
                <div className="group relative h-[500px] w-full overflow-hidden rounded-xl bg-muted">
                  {}
                  <div className="absolute inset-0">
                    <img src={event.image} alt={event.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent opacity-80" />
                  </div>

                  {}
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <div className="backdrop-blur-md bg-white/5 border border-white/10 rounded-lg p-5 shadow-lg transition-all duration-300 group-hover:bg-white/10">
                      <div className="flex items-center gap-2 text-primary mb-2">
                        <SafeHydrate><Calendar className="h-4 w-4" /></SafeHydrate>
                        <span className="text-sm font-medium uppercase tracking-wider text-primary">
                          {event.date}
                        </span>
                      </div>
                      
                      <h3 className="text-xl font-bold text-white mb-1 line-clamp-2">
                        {event.title}
                      </h3>
                      
                      <div className="flex items-center gap-2 text-gray-300 text-sm mb-4">
                        <MapPin className="h-3 w-3" />
                        <span>{event.location}</span>
                      </div>

                      <Link to={event.link} className="block w-full">
                        <Button className="w-full bg-white text-black hover:bg-gray-200 border-none">
                          Register Now
                        </Button>
                      </Link>
                    </div>
                  </div>
                </div>
              </CarouselItem>)}
          </CarouselContent>

          {}
          <div className="mt-8 flex items-center justify-between gap-8">
            <div className="flex-1 max-w-md">
              <Progress value={progressValue} className="h-1 bg-white/10" />
            </div>
            
            <div className="flex items-center gap-2">
              <Button variant="outline" size="icon" className="rounded-full border-white/20 bg-transparent text-white hover:bg-white/10 hover:text-white disabled:opacity-50" onClick={() => api?.scrollPrev()} disabled={!api?.canScrollPrev()}>
                <ArrowLeft className="h-4 w-4" />
                <span className="sr-only">Previous slide</span>
              </Button>
              <Button variant="outline" size="icon" className="rounded-full border-white/20 bg-transparent text-white hover:bg-white/10 hover:text-white disabled:opacity-50" onClick={() => api?.scrollNext()} disabled={!api?.canScrollNext()}>
                <ArrowRight className="h-4 w-4" />
                <span className="sr-only">Next slide</span>
              </Button>
            </div>
          </div>
        </Carousel>
        
        <div className="mt-8 md:hidden">
            <Link to="/JoinUsPage" className="w-full block">
              <Button variant="outline" className="w-full border-white/20 text-white hover:bg-white/10 hover:text-white">
                View Full Calendar
              </Button>
            </Link>
        </div>
      </div>
    </section>;
}